import { ActionIcon, Anchor, Flex, Text, Tooltip } from '@mantine/core';
import { HeaderContext } from '@tanstack/react-table';
import { ColumnHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader.tsx';
import {
	createSingleLineHeader,
	SINGLE_LINE_STYLE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/singleLine.ts';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { SecurityType } from '@dealstack/shared/lib/generated';
import { InvestorHoldingRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.types.ts';
import { canExpandVesting } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.utils.ts';
import { AmountCell } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/HoldingsCard.styles.tsx';

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	getSecurityTypeTranslation: (type: SecurityType) => string;
	directlyLabel: string;
	heldColumn?: 'via' | 'for';
	onOpenTransactions?: (row: InvestorHoldingRow) => void;
}

const hintedHeader =
	({
		label,
		hint,
		enableSorting = true,
	}: {
		label: string;
		hint: string;
		enableSorting?: boolean;
	}) =>
	(context: HeaderContext<InvestorHoldingRow, unknown>) => (
		<ColumnHeader context={context} enableSorting={enableSorting}>
			<Flex align="center" justify="flex-end" gap="0.25rem" w="100%" miw={0}>
				<Text fz={14} fw={600} c="dark.8" truncate style={SINGLE_LINE_STYLE}>
					{label}
				</Text>

				<Tooltip label={hint} multiline maw={260}>
					<Flex
						align="center"
						style={{ flexShrink: 0 }}
						onClick={(event) => event.stopPropagation()}
					>
						<Icon variant="information" size="lg" />
					</Flex>
				</Tooltip>
			</Flex>
		</ColumnHeader>
	);

const amountCell = (value: number | null, currency: InvestorHoldingRow['currency']) => (
	<AmountCell>
		<Text style={SINGLE_LINE_STYLE}>
			<AmountFormatted
				value={value}
				currency={currency}
				decimalScale={2}
				showZero
				showNegative
			/>
		</Text>

		{!(value != null && value < 0) && <span className="paren-gutter">)</span>}
	</AmountCell>
);

export const getInvestorHoldingsTableColumns = ({
	tMipManage,
	getSecurityTypeTranslation,
	directlyLabel,
	heldColumn = 'via',
	onOpenTransactions,
}: Props): ExtendedColumnDef<InvestorHoldingRow>[] => {
	const heldLabel =
		heldColumn === 'for'
			? tMipManage('investors.details.holdings_table.columns.held_for')
			: tMipManage('investors.details.holdings_table.columns.held_via');

	const transactionsColumn: ExtendedColumnDef<InvestorHoldingRow>[] = onOpenTransactions
		? [
				createTextColumn<InvestorHoldingRow>({
					id: 'transactions',
					size: 130,
					minSize: 130,
					accessorKey: 'transactionCount',
					header: createSingleLineHeader<InvestorHoldingRow>(
						tMipManage('investors.details.holdings_table.columns.transactions')
					),
					columnLabel: tMipManage(
						'investors.details.holdings_table.columns.transactions'
					),
					cell: ({ row }) => {
						const count = row.original.transactionCount;

						if (!count) {
							return <Text c="dark.0">{LONG_DASH}</Text>;
						}

						return (
							<Anchor
								component="button"
								type="button"
								fz={14}
								underline="always"
								maw="100%"
								truncate
								style={SINGLE_LINE_STYLE}
								onClick={(event) => {
									event.stopPropagation();
									onOpenTransactions(row.original);
								}}
								data-testid={makeComplexDataTestId(
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTIONS_LINK,
									row.original.id
								)}
							>
								{count === 1
									? tMipManage(
											'investors.details.holdings_table.transactions_count_one'
										)
									: tMipManage(
											'investors.details.holdings_table.transactions_count_other',
											{ count }
										)}
							</Anchor>
						);
					},
				}),
			]
		: [];

	return [
		createTextColumn<InvestorHoldingRow>({
			id: 'asset',
			size: 220,
			minSize: 180,
			accessorFn: (row) => row.issuerName,
			header: createSingleLineHeader<InvestorHoldingRow>(
				tMipManage('investors.details.holdings_table.columns.asset')
			),
			columnLabel: tMipManage('investors.details.holdings_table.columns.asset'),
			cell: ({ row }) => (
				<Flex direction="column" miw={0}>
					<Text fz={14} c="dark.9" truncate style={SINGLE_LINE_STYLE}>
						{row.original.issuerName}
					</Text>

					<Text fz={12} c="dark.5" truncate style={SINGLE_LINE_STYLE}>
						{`${getSecurityTypeTranslation(row.original.securityType)} • ${row.original.securityName}`}
					</Text>
				</Flex>
			),
		}),

		createTextColumn<InvestorHoldingRow>({
			id: 'heldVia',
			size: 130,
			minSize: 120,
			accessorFn: (row) =>
				heldColumn === 'for'
					? row.heldForName ?? LONG_DASH
					: row.heldViaName ?? directlyLabel,
			header: createSingleLineHeader<InvestorHoldingRow>(heldLabel),
			columnLabel: heldLabel,
		}),

		createNumberColumn<InvestorHoldingRow>({
			id: 'units',
			size: 90,
			minSize: 90,
			accessorKey: 'units',
			header: createSingleLineHeader<InvestorHoldingRow>(
				tMipManage('investors.details.holdings_table.columns.units'),
				{
					align: 'right',
				}
			),
			columnLabel: tMipManage('investors.details.holdings_table.columns.units'),
			cell: ({ row }) => (
				<Flex justify="flex-end" w="100%">
					<Text style={SINGLE_LINE_STYLE}>
						{formatNumber({ value: row.original.units })}
					</Text>
				</Flex>
			),
		}),

		createNumberColumn<InvestorHoldingRow>({
			id: 'totalCostActual',
			size: 150,
			minSize: 150,
			accessorKey: 'totalCostActual',
			header: hintedHeader({
				label: tMipManage('investors.details.holdings_table.columns.total_cost_act'),
				hint: tMipManage('investors.details.holdings_table.columns.total_cost_act_hint'),
			}),
			columnLabel: tMipManage('investors.details.holdings_table.columns.total_cost_act'),
			cell: ({ row }) => amountCell(row.original.totalCostActual, row.original.currency),
		}),

		createNumberColumn<InvestorHoldingRow>({
			id: 'totalCostAverage',
			size: 150,
			minSize: 150,
			accessorKey: 'totalCostAverage',
			header: hintedHeader({
				label: tMipManage('investors.details.holdings_table.columns.total_cost_ave'),
				hint: tMipManage('investors.details.holdings_table.columns.total_cost_ave_hint'),
			}),
			columnLabel: tMipManage('investors.details.holdings_table.columns.total_cost_ave'),
			cell: ({ row }) => amountCell(row.original.totalCostAverage, row.original.currency),
		}),

		createNumberColumn<InvestorHoldingRow>({
			id: 'costPerUnitAverage',
			size: 170,
			minSize: 170,
			accessorKey: 'avgCostPerUnit',
			header: hintedHeader({
				label: tMipManage('investors.details.holdings_table.columns.cost_per_unit_ave'),
				hint: tMipManage('investors.details.holdings_table.columns.cost_per_unit_ave_hint'),
				enableSorting: false,
			}),
			columnLabel: tMipManage('investors.details.holdings_table.columns.cost_per_unit_ave'),
			enableSorting: false,
			cell: ({ row }) => amountCell(row.original.avgCostPerUnit, row.original.currency),
		}),

		createNumberColumn<InvestorHoldingRow>({
			id: 'costPerUnitActual',
			size: 180,
			minSize: 180,
			accessorKey: 'actualCostPerUnit',
			header: hintedHeader({
				label: tMipManage('investors.details.holdings_table.columns.cost_per_unit_act'),
				hint: tMipManage('investors.details.holdings_table.columns.cost_per_unit_act_hint'),
			}),
			columnLabel: tMipManage('investors.details.holdings_table.columns.cost_per_unit_act'),
			cell: ({ row }) => amountCell(row.original.actualCostPerUnit, row.original.currency),
		}),

		createNumberColumn<InvestorHoldingRow>({
			id: 'currentValue',
			size: 120,
			minSize: 120,
			accessorKey: 'currentValue',
			header: createSingleLineHeader<InvestorHoldingRow>(
				tMipManage('investors.details.holdings_table.columns.current_value'),
				{ align: 'right', enableSorting: false }
			),
			columnLabel: tMipManage('investors.details.holdings_table.columns.current_value'),
			enableSorting: false,
			cell: ({ row }) => amountCell(row.original.currentValue, row.original.currency),
		}),

		...transactionsColumn,

		{
			id: 'vesting',
			header: () => (
				<Text fz={14} fw={600} c="dark.8" truncate style={SINGLE_LINE_STYLE}>
					{tMipManage('investors.details.holdings_table.columns.vesting')}
				</Text>
			),
			columnLabel: tMipManage('investors.details.holdings_table.columns.vesting'),
			enableSorting: false,
			enableColumnFilter: false,
			size: 80,
			minSize: 80,
			maxSize: 80,
			cell: ({ row }) => {
				if (!canExpandVesting(row.original)) {
					return (
						<Flex justify="center" w="100%">
							<Text c="dark.0">{LONG_DASH}</Text>
						</Flex>
					);
				}

				return (
					<Flex justify="center" w="100%">
						<ActionIcon
							variant="subtle"
							size="lg"
							c="dark.8"
							onClick={() => row.toggleExpanded()}
							data-testid={makeComplexDataTestId(
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_VESTING_TOGGLE,
								row.original.securityId
							)}
						>
							<Icon
								variant={row.getIsExpanded() ? 'caret-up' : 'caret-down'}
								size="lg"
							/>
						</ActionIcon>
					</Flex>
				);
			},
		},
	];
};
